/* eslint-disable @typescript-eslint/no-empty-function */

import React, {
    ReactNode,
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';
import Bugsnag from '@bugsnag/js';
import { usePricingContextIfProvided } from '@99designs/design-services-common';
import { useProductOptionsPricingQuery, useRequestQuery } from '../../graph';
import { LogomakerAsset } from '../BriefForm';
import { RecentlySelectedProduct } from '../BriefForm/FormFields/ClientConfiguredFields/ProductDropdown/utils';
import {
    BriefProductFragment,
    BriefQuery,
    BriefRequestFragment,
    StructuredBriefFragment,
} from '../BriefForm/brief.generated';
import { isBifrostBrief } from './isBifrostBrief';

const MAX_RECENT_PRODUCTS = 16;

/**
 * Default to show taxed inclusive price
 */
const DEFAULT_VAT_INCLUSIVE = true;

export type NormalisedBriefProduct = Omit<
    BriefProductFragment,
    'fulfilmentStrategy' | 'name' | '__typename' | 'pricing' | 'key' | 'cimpressProductConfig'
> & {
    pricing: Omit<BriefProductFragment['pricing'], 'earningsInUsd'>;
    key: string | null;
};

export type Brief = {
    request: BriefRequestFragment | null;
    product: BriefProductFragment | null;
} & StructuredBriefFragment;

interface BriefContextProps {
    product?: NormalisedBriefProduct;
    printProductMpvId?: string;
    isRequestQueryLoading: boolean;
    updateProduct: (product?: NormalisedBriefProduct) => void;
    onCompleted: (data: BriefQuery) => void;
    recentProducts?: RecentlySelectedProduct[];
    addRecentProduct: (product: RecentlySelectedProduct) => void;
    isUpdatingProduct: boolean;
    setIsUpdatingProduct: React.Dispatch<React.SetStateAction<boolean>>;
    selectedProductOptions: Record<string, string>;
    config: BriefQuery['brief']['config'] | null;
    correlationId: BriefQuery['brief']['correlationId'];
    serviceProductMpvId: string;
    logomakerAsset?: LogomakerAsset;
    isRequestClaimed?: boolean | null;
    isBifrostBrief?: boolean;
}

export const BriefContext = createContext<BriefContextProps | undefined>(undefined);

interface BriefProviderProps {
    children: ReactNode;
}

export const BriefProvider: React.FC<BriefProviderProps> = ({ children }) => {
    const [product, setProduct] = useState<NormalisedBriefProduct>();
    const [printProductMpvId, setPrintProductMpvId] = useState<string | undefined>(undefined);
    const [selectedProductOptions, setSelectedProductOptions] = useState<Record<string, string>>(
        {}
    );
    const [config, setConfig] = useState<BriefQuery['brief']['config'] | null>(null);
    const [briefData, setBriefData] = useState<BriefQuery['brief'] | null>(null);
    const pricingContext = usePricingContextIfProvided();

    // Get request data for dynamic pricing (only if we have a request ID)
    const requestId = briefData?.request?.id;
    const { data: requestData, loading: isRequestQueryLoading } = useRequestQuery({
        variables: { id: requestId || '' },
        skip: !requestId,
        fetchPolicy: 'cache-and-network',
        errorPolicy: 'ignore',
    });

    useEffect(() => {
        setPrintProductMpvId(requestData?.request?.facts?.printProductMpvid || undefined);
    }, [requestData?.request?.facts?.printProductMpvid]);

    const logomakerAsset = briefData?.request?.optionalFacts
        ? {
              id: briefData?.request?.optionalFacts?.logomakerEntityId,
              sig: briefData?.request?.optionalFacts?.logomakerEntitySig,
          }
        : undefined;

    // Convert serviceProductOptions to selections format for pricing query
    const pricingSelections = useMemo(() => {
        if (!requestData?.request?.facts?.serviceProductOptions) {
            return [];
        }
        return requestData.request.facts.serviceProductOptions.map(({ key, value }) => ({
            key,
            value,
        }));
    }, [requestData?.request?.facts?.serviceProductOptions]);

    // Get service product details for pricing query
    const serviceProduct = requestData?.request?.expertService?.serviceProduct;
    const shouldFetchPricing = serviceProduct?.productKey && pricingSelections.length > 0;

    // Call pricing query with serviceProductOptions
    const {
        data: pricingData,
        loading: pricingLoading,
        error: pricingError,
    } = useProductOptionsPricingQuery({
        variables: {
            input: {
                productKey: serviceProduct?.productKey || '',
                pimMerchantVersion: serviceProduct?.pimMerchantVersion || 1,
                selections: pricingSelections,
                pivotSelections: [],
            },
        },
        skip: !shouldFetchPricing,
        errorPolicy: 'ignore',
    });

    // TODO: Recent products should be stored in the browser's local storage.

    const [recentProducts, setRecentProducts] = useState<RecentlySelectedProduct[]>([]);

    const updateProduct = useCallback(
        (newProduct?: NormalisedBriefProduct) => {
            setProduct(newProduct);
        },
        [setProduct]
    );

    const [isUpdatingProduct, setIsUpdatingProduct] = useState(false);

    // Memoize the new pricing values only when they actually change and differ from current product
    const newPricingValues = useMemo(() => {
        if (!product || !pricingData?.selectionsPricing || pricingError) {
            return null;
        }

        const includeTax = pricingContext?.vatInclusive ?? DEFAULT_VAT_INCLUSIVE;
        const dynamicPrice = includeTax
            ? pricingData.selectionsPricing.listPrice.taxed
            : pricingData.selectionsPricing.listPrice.untaxed;

        const newAmountInCents = Math.round(dynamicPrice * 100);
        const newCurrency = pricingData.selectionsPricing.currency;

        const hasCurrentPricing =
            product.pricing.amount.amountInCents === newAmountInCents &&
            product.pricing.amount.currency === newCurrency;

        if (hasCurrentPricing) {
            return null;
        }

        return {
            amountInCents: newAmountInCents,
            currency: newCurrency,
        };
    }, [
        pricingData,
        pricingError,
        product?.pricing.amount.amountInCents,
        product?.pricing.amount.currency,
        pricingContext?.vatInclusive,
    ]);

    // Update product with dynamic pricing when new pricing values are available
    useEffect(() => {
        if (!newPricingValues || !product) {
            return;
        }

        const updatedProduct = {
            ...product,
            pricing: {
                ...product.pricing,
                amount: {
                    __typename: 'Money' as const,
                    amountInCents: newPricingValues.amountInCents,
                    currency: newPricingValues.currency,
                },
            },
        };

        updateProduct(updatedProduct);
    }, [newPricingValues, product, updateProduct]);

    const serviceProductMpvId = requestData?.request.expertService?.serviceProduct?.mpvId || '';

    return (
        <BriefContext.Provider
            value={{
                product,
                printProductMpvId,
                isRequestQueryLoading,
                recentProducts,
                selectedProductOptions,
                config,
                logomakerAsset,
                isRequestClaimed: requestData?.request.isClaimed,
                isBifrostBrief: briefData
                    ? isBifrostBrief(briefData as StructuredBriefFragment)
                    : undefined,
                correlationId: briefData?.correlationId ?? '',
                serviceProductMpvId,
                updateProduct,
                addRecentProduct: (product: RecentlySelectedProduct) => {
                    setRecentProducts((prev) => {
                        if (prev.length >= MAX_RECENT_PRODUCTS) {
                            prev.splice(MAX_RECENT_PRODUCTS - 1, 1);
                        }

                        const index = prev.findIndex((p) => p.mpvId === product.mpvId);

                        if (index !== -1) {
                            prev.splice(index, 1);
                        }

                        return [product, ...prev];
                    });
                },
                onCompleted: (data: BriefQuery) => {
                    // The product should not update if it already exists as it may be newer than the cached
                    // data fetched from this query.

                    if (product || !data) {
                        return;
                    }

                    setConfig(data.brief.config ?? null);
                    setBriefData(data.brief);

                    const prod = formatProductFromProdOrService(
                        data.brief,
                        pricingContext?.vatInclusive
                    );
                    if (prod) {
                        updateProduct(prod);
                    }
                    const productOptions = data.brief.dynamicFields.find(
                        (df) => df.id === 'productOptions'
                    );
                    if (!productOptions) {
                        return;
                    }
                    if (productOptions.__typename !== 'ClientConfiguredField') {
                        Bugsnag.notify('Expected productOption to be a ClientConfiguredField');
                        return;
                    }
                    if (!productOptions.clientConfiguredValue) {
                        return;
                    }
                    try {
                        const parsedValue = JSON.parse(productOptions.clientConfiguredValue);
                        setSelectedProductOptions(parsedValue);
                    } catch (e) {
                        Bugsnag.notify('Failed to parse product options');
                    }
                },
                isUpdatingProduct,
                setIsUpdatingProduct,
            }}
        >
            {children}
        </BriefContext.Provider>
    );
};

export function formatProductFromProdOrService(
    brief: {
        request: BriefRequestFragment | null;
        product: BriefProductFragment | null;
    } & StructuredBriefFragment,
    vatInclusive = DEFAULT_VAT_INCLUSIVE
) {
    if (brief.product && isBifrostBrief(brief)) {
        return {
            title: brief.product.title,
            details: brief.product.details,
            pricing: brief.product.pricing,
            key: brief.product.key,
            name: brief.product.name,
            fulfilmentMpvId: null,
        };
    }
    if (brief.product && brief.fulfilment) {
        return {
            title: brief.product.title,
            details: brief.product.details,
            pricing: brief.product.pricing,
            key: brief.fulfilment.productKey,
            fulfilmentMpvId: brief.fulfilment.mpvId,
        };
    }
    return expertServiceToProduct(brief.request ?? undefined, vatInclusive);
}

export function MockBriefProvider({
    children,
    value = {},
}: {
    children: ReactNode;
    value?: Partial<BriefContextProps>;
}) {
    return (
        <BriefContext.Provider
            value={{
                product: {
                    title: 'Mock Product',
                    details: [],
                    pricing: {
                        __typename: 'FixedPrice',
                        amount: {
                            __typename: 'Money',
                            amountInCents: 1000,
                            currency: 'USD',
                        },
                    },
                    key: 'mock-key',
                    fulfilmentMpvId: 'mock-fulfilment-mpv-id',
                },
                isRequestQueryLoading: false,
                isBifrostBrief: false,
                updateProduct: () => {},
                onCompleted: () => {},
                recentProducts: [],
                addRecentProduct: () => {},
                isUpdatingProduct: false,
                setIsUpdatingProduct: () => {},
                selectedProductOptions: {},
                config: null,
                correlationId: 'mock-correlation-id',
                serviceProductMpvId: '',
                logomakerAsset: { id: undefined, sig: undefined },
                ...value,
            }}
        >
            {children}
        </BriefContext.Provider>
    );
}

export function expertServiceToProduct(
    request?: BriefRequestFragment,
    vatInclusive?: boolean
): Omit<
    BriefProductFragment,
    '__typename' | 'fulfilmentStrategy' | 'name' | 'cimpressProductConfig'
> | null {
    const es = request?.expertService;

    if (!request || !es?.printProduct) {
        return null;
    }

    const earnings = request.facts?.expertEarningsInUsdCents || 0;
    const price = vatInclusive ? es.price.listPrice.taxed : es.price.listPrice.untaxed;

    return {
        title: es.title,
        details:
            es.productDetails?.inclusions.map((i) => ({
                __typename: 'ProductPackageDetails',
                title: i.description,
            })) ?? [],
        pricing: {
            __typename: 'FixedPrice',
            amount: {
                __typename: 'Money',
                amountInCents: Math.round(price * 100),
                currency: es.price.currency,
            },
            earningsInUsd: earnings,
        },
        key: es.printProduct.mpvId,
        fulfilmentMpvId: es.printProduct.mpvId,
    };
}

export const useBriefContext = () => {
    const context = useContext(BriefContext);

    if (!context) {
        throw new Error('useBriefContext must be used within a BriefProvider');
    }

    return context;
};

/**
 * Updates the product in the brief context when the brief changes
 */
export const useUpdateProductOnBriefChange = (brief?: Brief) => {
    const { updateProduct, product } = useBriefContext();
    const pricingContext = usePricingContextIfProvided();

    useEffect(() => {
        if (!brief) {
            return;
        }
        const briefProduct = formatProductFromProdOrService(brief, pricingContext?.vatInclusive);
        if (!briefProduct || areTheSameProducts(briefProduct, product)) {
            // Don't update to avoid unnecessary re-renders
            return;
        }
        updateProduct(briefProduct);
    }, [brief, pricingContext, updateProduct, product]);
};

function areTheSameProducts(productA: unknown, productB: unknown) {
    return JSON.stringify(productA) === JSON.stringify(productB);
}
